/* eslint-disable */
import React, { useContext, MouseEvent, useEffect, useState, useRef } from 'react';

// MUI
import Button from '@mui/material/Button';
import Tooltip from '@mui/material/Tooltip';
import { useTheme } from '@mui/material';
import useMediaQuery from '@mui/material/useMediaQuery';
import Fab from '@mui/material/Fab';

// Custom Components
import UserContext from '@/context/UserContext/UserContext';

// Types
import { ITableHeaderButtonProps } from '@/@types/ui/Table';

const TableHeaderButton: React.FC<ITableHeaderButtonProps> = (props) => {
  const userContext = useContext(UserContext);
  const {
    onClick,
    needPermission,
    smallButtons,
    disabled,
    tooltip,
    variant = 'contained',
    checked = false,
    ...otherProps
  } = props;

  const theme = useTheme();
  const mdDown = useMediaQuery(theme.breakpoints.down(1050));

  const [toolbarWidth, setToolbarWidth] = useState<number>(Infinity);
  const buttonRef = useRef<HTMLButtonElement | null>(null);

  const handleClick = (evt: MouseEvent<HTMLButtonElement>) => {
    onClick(evt);
  };

  useEffect(() => {
    const buttonElement = buttonRef.current;

    // Very hacky, but passing parent ref all the way to every TableHeaderButton is too much work
    // Find the closest parent with the class `.MuiToolbar-root`
    const toolbarElement = buttonElement?.closest('.MuiToolbar-root');

    if (toolbarElement) {
      // Create a ResizeObserver to monitor changes to the element's size
      const resizeObserver = new ResizeObserver((entries) => {
        for (let entry of entries) {
          setToolbarWidth(entry.contentRect.width);
        }
      });

      resizeObserver.observe(toolbarElement);

      // Cleanup
      return () => {
        resizeObserver.disconnect();
      };
    }
    return;
  }, []);

  const shouldRenderFab = (smallButtons || mdDown || toolbarWidth < 1450);

  if (needPermission && userContext && !userContext.hasAnyPermission(needPermission)) {
    return null;
  }

  return shouldRenderFab ? (
    <Tooltip title={tooltip} arrow placement="bottom">
      <span style={{ display: 'inline-flex' }}>
        <Fab
          ref={buttonRef}
          color="primary"
          size="small"
          onClick={handleClick}
          disabled={disabled ?? false}
          sx={{
            height: 28,
            minHeight: 28,
            width: 28,
            minWidth: 28,
            mx: '4px',
            padding: 0,

            ...(variant === 'outlined'
              ? {
                  backgroundColor: 'transparent',
                  color: 'primary.main',
                  border: '1px solid',
                  borderColor: 'primary.main',
                  boxShadow: 'none',
                  '&:hover': {
                    backgroundColor: 'action.hover',
                    boxShadow: 'none',
                  },
                  '&.Mui-disabled': {
                    backgroundColor: 'transparent',
                    color: 'action.disabled',
                    borderColor: 'action.disabled',
                    boxShadow: 'none',
                  },
                }
              : {}),

            ...(checked === true
              ? {
                  backgroundColor: 'action.focus',
                }
              : {}),

            '& .MuiSvgIcon-root': {
              fontSize: 18,
            },
          }}
        >
          {otherProps.startIcon}
        </Fab>
      </span>
    </Tooltip>
  ) : (
    <Button
      ref={buttonRef}
      color="primary"
      size="small"
      variant={variant}
      {...otherProps}
      onClick={handleClick}
      disabled={disabled ?? false}
      sx={{
        minHeight: 32,
        ml: 1,
        mr: 0,
      }}
    >
      {props.children}
    </Button>
  );
};

export default TableHeaderButton;
